import type { KitchenBoardPlate } from "@/lib/api/types";

/**
 * WHAT "LATE" MEANS ON THE KITCHEN BOARD (UX-round D47, made the store's own in
 * B125/D115).
 * «UX-round Dn» = the 2026-09-17 UX round's own numbering (`.roundtable/2026-09-17-restaurant-ux/decisions.md`), not §11's.
 *
 * The board had no threshold at all. `formatWaiting` rendered «من 10 د» in the
 * same grey, the same weight and the same 15px as the round number beside it,
 * third in a metadata row — so a 4-minute plate and a 50-minute plate were
 * pixel-identical from two metres. D47 gave each lane a fixed limit (10 waiting,
 * 20 on the pan, 25 for a READY plate timed from its start).
 *
 * ─── WHY THE THRESHOLD IS PER LANE ───────────────────────────────────────────
 * "20 minutes queued" and "20 minutes on the grill" are different emergencies.
 * A plate nobody has STARTED after ten minutes is a plate the table is already
 * asking about; the same ten minutes on the pan is an ordinary main course.
 *
 * ─── AND NOW PER PLATE (B125/D115) ──────────────────────────────────────────
 * The limits are the store's targets, frozen on the plate and its bill and
 * carried on the payload (`plate.targets`): waiting to start, the DISH's own
 * preparing target (a mixed grill is not late at twenty minutes, a salad is),
 * and ready to table. The service speed report judges the same plate by the
 * same frozen numbers with the same rule — strictly OVER, to the millisecond —
 * so the amber card here and the late count there can never disagree.
 *
 * The READY lane is now timed from `readyAt`, which the payload carries; the
 * old proxy (timing it from `preparingAt` against a limit set clear of a
 * normal cook) is gone.
 */

export type PlateLane = "QUEUED" | "PREPARING" | "READY";

/**
 * The store targets' own defaults (D115: waiting 10, preparing 20, pass 5) —
 * what a plate is judged by when the payload carries no `targets` at all,
 * which an API from before B125 does not. The board then behaves as it did
 * before the targets existed instead of failing to draw.
 */
export const DEFAULT_PLATE_TARGETS = {
  queueMinutes: 10,
  prepMinutes: 20,
  passMinutes: 5,
} as const;

/** A plate's targets, or the defaults when the API sent none. */
export function targetsOf(
  plate: Pick<KitchenBoardPlate, "targets">,
): NonNullable<KitchenBoardPlate["targets"]> {
  return plate.targets ?? DEFAULT_PLATE_TARGETS;
}

type LatenessPlate = Pick<
  KitchenBoardPlate,
  "fulfillmentState" | "firedAt" | "preparingAt" | "readyAt" | "targets"
>;

/**
 * Where the plate's clock starts in its lane: "how long has the table been
 * waiting", "how long has this been cooking" and "how long has it sat on the
 * pass" are three different questions, and the lane says which one is asked.
 */
function laneStart(plate: LatenessPlate): number | null {
  const since =
    plate.fulfillmentState === "QUEUED"
      ? plate.firedAt
      : plate.fulfillmentState === "PREPARING"
        ? plate.preparingAt
        : plate.fulfillmentState === "READY"
          ? plate.readyAt
          : null;
  // A plate can genuinely have no stamp (a start that predates the timestamp
  // columns). Saying nothing beats "NaN".
  if (!since) return null;
  const parsed = new Date(since).getTime();
  return Number.isNaN(parsed) ? null : parsed;
}

/** The plate's own clock in its lane, in whole minutes, or null when it has none. */
export function waitingMinutes(
  plate: LatenessPlate,
  now: number,
): number | null {
  const since = laneStart(plate);
  if (since === null) return null;
  return Math.max(0, Math.floor((now - since) / 60_000));
}

/** This plate's limit in its lane, in minutes, or null outside the three lanes. */
export function lateAfterMinutes(plate: LatenessPlate): number | null {
  switch (plate.fulfillmentState) {
    case "QUEUED":
      return targetsOf(plate).queueMinutes;
    case "PREPARING":
      return targetsOf(plate).prepMinutes;
    case "READY":
      return targetsOf(plate).passMinutes;
    default:
      return null;
  }
}

/**
 * Is this plate past its own target? Strictly over, to the millisecond — the
 * report's rule — so a plate at exactly ten minutes against a ten-minute
 * target is on time in both places.
 *
 * A plate with no clock is never late: the board would otherwise shout about a
 * plate whose timestamp is simply missing, which teaches a cook to ignore the
 * one signal this whole change exists to create.
 */
export function isPlateLate(plate: LatenessPlate, now: number): boolean {
  const limit = lateAfterMinutes(plate);
  const since = laneStart(plate);
  if (limit === null || since === null) return false;
  return now - since > limit * 60_000;
}

/**
 * WHETHER A CARD'S CLOCK IS RED (T16, D170): only when a plate on it is late.
 * The waiting lane's clock used to be red whatever its age, so a two-minute
 * ticket shouted like a twenty-minute one and red stopped meaning anything.
 */
export function isClockAlert(
  plates: readonly LatenessPlate[],
  now: number,
): boolean {
  return plates.some((plate) => isPlateLate(plate, now));
}
